import { ComputedRef, Ref, computed, inject, provide, ref, watch } from "vue";

export type RoleType = "Home_user" | "Tutor" | "Other" | "School";
export type FieldType =
  | "address"
  | "org_id"
  | "organisation"
  | "postcode"
  | "school_size";

export type ContactCaptureContext = {
  address: Ref<string>;
  country: Ref<number | undefined | null>;
  manualSchoolEntry: Ref<boolean>;
  org_id: Ref<string | null>;
  organisation: Ref<string | undefined | null>;
  postcode: Ref<string | undefined | null>;
  role: Ref<RoleType | undefined | null>;
  isCountryAddressSearchable: ComputedRef<boolean>;
  isSchoolSearchAllowed: ComputedRef<boolean>;
  excludeRoles: Ref<RoleType[]>;
  isFieldVisible: (name: string) => boolean;
};

export type InitialValues = {
  address?: string;
  country?: number;
  manualSchoolEntry?: boolean;
  org_id?: string;
  organisation?: string;
  postcode?: string;
  role?: RoleType;
  excludeRoles?: RoleType[];
};

export const contactCaptureContextKey = "contact-capture";

// Map the hidden form fields of each role.
const hiddenRoleFields: Record<RoleType, FieldType[]> = {
  Home_user: ["org_id", "organisation", "school_size"],
  Tutor: ["org_id", "organisation", "school_size"],
  Other: ["org_id", "school_size"],
  School: ["school_size"],
};

const schoolAddressFields = ["address", "organisation", "postcode"];

// ID of countries where search is available for addresses and organisations.
const allowedSchoolSearchCountries = [1, 5, 27, 207];

/**
 * Utilities for forms that contain address fields.
 */
export function useContactCaptureProvider(
  prefix = "",
  initialValues?: InitialValues
): ContactCaptureContext {
  const address = ref(initialValues?.address ?? "");
  const country = ref<number | null>(initialValues?.country ?? null);
  const manualSchoolEntry = ref(initialValues?.manualSchoolEntry ?? false);
  const org_id = ref(initialValues?.org_id ?? null);
  const organisation = ref(initialValues?.organisation ?? null);
  const postcode = ref(initialValues?.postcode ?? null);
  const role = ref<RoleType | null>(initialValues?.role ?? null);
  const excludeRoles = ref<RoleType[]>(initialValues?.excludeRoles ?? []);

  const isCountryAddressSearchable = computed<boolean>(() => {
    return (
      !!country.value && allowedSchoolSearchCountries.includes(country.value)
    );
  });

  const isSchoolSearchAllowed = computed<boolean>(() => {
    return !manualSchoolEntry.value && isCountryAddressSearchable.value;
  });

  const isFieldVisible = (name: string) => {
    const isHiddenRoleField =
      role.value &&
      hiddenRoleFields[role.value].includes(
        <FieldType>name.replace(prefix, "")
      );
    const isSchoolAddressField = schoolAddressFields.includes(
      name.replace(prefix, "")
    );
    const isRoleSchool = role.value === "School";

    return (
      !isHiddenRoleField &&
      !(isRoleSchool && isSchoolSearchAllowed.value && isSchoolAddressField) // hide address fields when search is available.
    );
  };

  watch(
    role,
    () => {
      manualSchoolEntry.value = false;
      org_id.value = null;
      address.value = "";
      postcode.value = null;
    },
    { immediate: true }
  );

  watch(
    manualSchoolEntry,
    (value) => {
      if (value) {
        org_id.value = null;
      }
    },
    { immediate: true }
  );

  const context: ContactCaptureContext = {
    address,
    country,
    manualSchoolEntry,
    org_id,
    organisation,
    postcode,
    role,
    isCountryAddressSearchable,
    isSchoolSearchAllowed,
    excludeRoles,
    isFieldVisible,
  };

  provide(contactCaptureContextKey, context);

  return context;
}

/**
 * Utility to inject {@link ContactCaptureContext}.
 */
export function useContactCaptureContext(): ContactCaptureContext {
  const ctx = inject<ContactCaptureContext | null>(
    contactCaptureContextKey,
    null
  );

  if (!ctx) {
    throw new Error(
      "useContactCaptureContext needs to be inside ContactCaptureContext."
    );
  }

  return ctx;
}
